import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ROLE_LABELS, ROLE_PORTAL_MAP } from "../lib/roles";
import { accessApi, authApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Seo } from "../components/cod/Seo";
import { CODLogoMark } from "../components/cod/Logo";

// ─── Portal Picker (multi-role users) ──────────────────────────────────────
export function PortalPicker() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white">Loading...</div>;
  if (!user) { navigate("/signin"); return null; }
  const roles = user.roles && user.roles.length ? user.roles : [user.role];
  return (
    <div className="min-h-screen bg-white" data-testid="portal-picker-page">
      <Seo title="Choose Your Workspace" description="Choose your Chicago Opportunity Desk workspace." path="/portal" noindex/>
      <div className="cod-stripe"/>
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Link to="/" className="inline-block mb-8"><CODLogoMark size={48} onLight/></Link>
        <p className="cod-eyebrow">Choose Your Workspace</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">Welcome back, {user.full_name.split(" ")[0]}.</h1>
        <div className="cod-rule-red mt-4 mb-8"/>
        <p className="text-slate-600 mb-8">You have access to multiple workspaces. Pick where you'd like to work today — you can switch anytime.</p>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="portal-options">
          {roles.map(r => (
            <button
              key={r}
              onClick={() => navigate(ROLE_PORTAL_MAP[r] || "/app/home")}
              data-testid={`portal-option-${r}`}
              className="text-left rounded-lg border border-slate-200 bg-white p-5 hover:border-[var(--cod-red)] hover:shadow-md transition"
            >
              <div className="font-semibold text-slate-900">{ROLE_LABELS[r] || r}</div>
              <div className="text-xs text-slate-600 mt-1">Enter {ROLE_LABELS[r] || r} workspace →</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── First-login Reset Password ────────────────────────────────────────────
export function ResetPassword() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [busy, setBusy] = useState(false);
  if (loading) return null;
  if (!user) { navigate("/signin"); return null; }
  const submit = async (e) => {
    e.preventDefault();
    if (p1 !== p2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await accessApi.resetPassword(p1);
      toast.success("Password updated. Welcome to COD.");
      // Refresh /me so must_reset_password clears in AuthProvider
      const me = await authApi.me();
      navigate(me.primary_portal || "/app/home");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-white flex items-center" data-testid="reset-password-page">
      <Seo title="Set Your Password" path="/reset-password" noindex/>
      <div className="cod-stripe absolute top-0 left-0 right-0"/>
      <div className="max-w-md mx-auto w-full px-4">
        <CODLogoMark size={48} onLight/>
        <p className="cod-eyebrow mt-8">First Sign-in</p>
        <h1 className="font-serif text-3xl text-slate-900 mt-2">Set your password.</h1>
        <div className="cod-rule-red mt-4 mb-6"/>
        <p className="text-sm text-slate-600 mb-6">Your COD account was created by an administrator. Choose a password only you know.</p>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>New Password</Label><Input data-testid="reset-p1" type="password" required minLength={8} value={p1} onChange={e=>setP1(e.target.value)} className="mt-1 h-11"/></div>
          <div><Label>Confirm Password</Label><Input data-testid="reset-p2" type="password" required minLength={8} value={p2} onChange={e=>setP2(e.target.value)} className="mt-1 h-11"/></div>
          <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full h-11 cod-bg-red">{busy ? "Saving..." : "Set Password & Continue"}</Button>
        </form>
      </div>
    </div>
  );
}


// ─── Graceful Access Denied with Request-Access workflow ────────────────────
export function AccessDenied({ moduleId, moduleName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await accessApi.request(moduleId || "unknown_module", reason);
      setSent(true);
      toast.success(res.message || "Request sent.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send request"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center" data-testid="access-denied-page">
      <Seo title="Access Not Yet Granted" path="/access-denied" noindex/>
      <div className="cod-stripe absolute top-0 left-0 right-0"/>
      <div className="max-w-lg mx-auto w-full px-4 py-16">
        <CODLogoMark size={48} onLight/>
        <p className="cod-eyebrow mt-8">Access Not Yet Granted</p>
        <h1 className="font-serif text-3xl text-slate-900 mt-2">This workspace isn't part of your current role.</h1>
        <div className="cod-rule-red mt-4 mb-6"/>
        <p className="text-slate-700 mb-2">You're signed in as <span className="font-semibold">{user?.full_name}</span> with the role <span className="font-semibold">{ROLE_LABELS[user?.role] || user?.role}</span>.</p>
        <p className="text-sm text-slate-600 mb-6">The <span className="font-semibold text-slate-900">{moduleName || moduleId}</span> module requires additional permissions. You can request access from a COD administrator below — they'll review it and get back to you.</p>
        {sent ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-5" data-testid="access-request-sent">
            <div className="font-semibold text-emerald-900">Request sent</div>
            <p className="text-sm text-emerald-800 mt-1">A COD administrator has been notified. You'll receive a notification once your access has been reviewed.</p>
            <div className="mt-4 flex gap-3">
              <Button onClick={()=>navigate(user?.primary_portal || "/app/home")} className="cod-bg-red" data-testid="access-back-primary">Return to my workspace</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Why do you need access? <span className="text-slate-500 font-normal">(optional)</span></Label>
              <textarea data-testid="access-reason" value={reason} onChange={e=>setReason(e.target.value)} rows={4} className="mt-1 w-full rounded-md border border-slate-300 p-3 text-sm" placeholder="A short note helps the administrator route your request faster."/>
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={busy} className="cod-bg-red" data-testid="access-request-submit">{busy ? "Sending..." : "Request Access"}</Button>
              <Button type="button" variant="outline" onClick={()=>navigate(user?.primary_portal || "/app/home")} data-testid="access-back">Back to my workspace</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
